/**
 * Avatar Upload Component
 *
 * A simple avatar upload component that allows users to:
 * - Upload profile images
 * - Preview uploaded images
 * - Remove/reset images
 */

import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

const AvatarUpload = ({
  onImageChange,
  currentImage,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation("auth");
  const [preview, setPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        onImageChange({ error: "Please select a valid image file" });
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        onImageChange({ error: "Image size must be less than 5MB" });
        return;
      }

      setIsProcessing(true);

      // Create FileReader to load image
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        setPreview(imageUrl);
        setIsProcessing(false);

        // Pass the file and preview URL to parent
        onImageChange({
          file: file,
          preview: imageUrl,
          error: null,
        });
      };
      reader.onerror = () => {
        onImageChange({ error: "Failed to read file" });
        setIsProcessing(false);
      };
      reader.readAsDataURL(file);
    },
    [onImageChange],
  );

  // Remove image
  const removeImage = useCallback(() => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onImageChange({ file: null, preview: null, error: null });
  }, [preview, onImageChange]);

  return (
    <div className="space-y-2">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {t("signUp.profilePicture")}
      </label>

      {/* Upload Area */}
      <div className="relative">
        {/* Preview or Upload Button */}
        {preview || currentImage?.preview ? (
          <div className="flex items-center space-x-4">
            {/* Avatar Preview */}
            <div className="relative w-20 h-20 rounded-full overflow-hidden bg-gray-700 border-2 border-gray-600">
              <img
                src={preview || currentImage.preview}
                alt="Avatar preview"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || isProcessing}
                className="px-3 py-1 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("signUp.changeImage")}
              </button>
              <button
                type="button"
                onClick={removeImage}
                disabled={disabled || isProcessing}
                className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("signUp.removeImage")}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isProcessing}
            className={`
              w-full p-6 border-2 border-dashed rounded-2xl
              ${
                error
                  ? "border-red-500/50 bg-red-500/10"
                  : "border-gray-600/50 bg-gray-800/30 hover:bg-gray-800/50"
              }
              text-gray-400 hover:text-white transition-all duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {isProcessing ? (
              <div className="flex flex-col items-center space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-400"></div>
                <span className="text-sm">{t("signUp.processing")}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                <span className="text-sm">
                  {t("signUp.uploadProfilePicture")}
                </span>
                <span className="text-xs text-gray-500">
                  {t("signUp.imageFormat")}
                </span>
              </div>
            )}
          </button>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || isProcessing}
        />
      </div>

      {/* Error Message */}
      {error && <p className="text-sm text-red-400 pl-1">{error}</p>}
    </div>
  );
};

export default AvatarUpload;
